/**
 * Ask a Question — Modal + Form Handler
 * ─────────────────────────────────────────────────────────────────
 * Opens a modal when the trigger button is clicked.
 * Validates the form client-side, then submits via AJAX to Shopify's
 * native /contact endpoint (form_type: "contact").
 *
 * Shopify automatically:
 *  - Emails the store owner with the question details
 *  - Sends a confirmation email to the customer (configured in
 *    Shopify Admin → Settings → Notifications → "Contact form")
 */

/* ── Open / Close ───────────────────────────────────────────── */

function openAskModal(id) {
  const overlay = document.getElementById(id);
  if (!overlay) return;
  overlay.removeAttribute('hidden');
  document.body.style.overflow = 'hidden';

  // Re-render hCaptcha widget if Shopify bot protection is enabled.
  // The widget doesn't initialize while the element has [hidden], so we
  // reset it after the modal becomes visible.
  setTimeout(() => {
    if (window.hcaptcha) {
      try {
        overlay.querySelectorAll('.h-captcha').forEach(el => {
          const widgetId = el.dataset.hcaptchaWidgetId;
          if (widgetId !== undefined) {
            hcaptcha.reset(widgetId);   // already rendered — just reset
          } else {
            hcaptcha.render(el);        // not yet rendered — render now
          }
        });
      } catch (e) { /* ignore if already rendered */ }
    }
  }, 50);

  // Focus first input
  const first = overlay.querySelector('input:not([type="hidden"]), textarea');
  if (first) setTimeout(() => first.focus(), 100);
}

function closeAskModal(id) {
  const overlay = document.getElementById(id);
  if (!overlay) return;
  overlay.setAttribute('hidden', '');
  document.body.style.overflow = '';

  // Reset hCaptcha so it's ready for the next open
  if (window.hcaptcha) {
    try {
      overlay.querySelectorAll('.h-captcha').forEach(el => {
        const widgetId = el.dataset.hcaptchaWidgetId;
        if (widgetId !== undefined) hcaptcha.reset(widgetId);
      });
    } catch (e) { /* ignore */ }
  }
}

/* ── Validation ─────────────────────────────────────────────── */

function validateForm(form) {
  let valid = true;

  // Clear previous errors
  form.querySelectorAll('.ask-question__error').forEach(el => el.textContent = '');
  form.querySelectorAll('.ask-question__input').forEach(el => el.classList.remove('is-invalid'));

  const name    = form.querySelector('[name="contact[name]"]');
  const email   = form.querySelector('[name="contact[email]"]');
  const message = form.querySelector('[name="contact[body]"]');

  if (name && !name.value.trim()) {
    setError(name, 'Please enter your name.');
    valid = false;
  }

  if (email) {
    if (!email.value.trim()) {
      setError(email, 'Please enter your email address.');
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
      setError(email, 'Please enter a valid email address.');
      valid = false;
    }
  }

  if (message && !message.value.trim()) {
    setError(message, 'Please enter your message.');
    valid = false;
  }

  return valid;
}

function setError(input, msg) {
  input.classList.add('is-invalid');
  const errorEl = input.closest('.ask-question__field')?.querySelector('.ask-question__error');
  if (errorEl) errorEl.textContent = msg;
}

/* ── Form submission ────────────────────────────────────────── */

async function submitForm(form, modalId) {
  const statusEl = form.querySelector('.ask-question__status');
  const submitBtn = form.querySelector('.ask-question__submit');
  const submitText = form.querySelector('.ask-question__submit-text');
  const spinner = form.querySelector('.ask-question__spinner');

  const data = new FormData(form);

  // Merge product context prefix into the message body
  const prefix = data.get('contact[body_prefix]') || '';
  const body   = data.get('contact[body]') || '';
  data.set('contact[body]', prefix + body);
  data.delete('contact[body_prefix]');

  // Debug — confirm token is present (remove after testing)
  console.log('[AskAQuestion] authenticity_token present:', !!data.get('authenticity_token'));
  console.log('[AskAQuestion] form_type:', data.get('form_type'));

  // Loading state
  submitBtn.disabled = true;
  submitText.textContent = 'Sending…';
  spinner.removeAttribute('hidden');
  if (statusEl) statusEl.setAttribute('hidden', '');

  try {
    const res = await fetch('/contact', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: data
    });

    if (res.ok) {
      // ── Success ──
      showStatus(statusEl, 'success',
        '✓ Your question has been sent! We\'ll get back to you shortly. A confirmation has been sent to your email.');

      // Hide fields, show only the status after success
      form.querySelector('.ask-question__fields').style.display = 'none';
      submitBtn.style.display = 'none';

      // Auto-close after 4 seconds
      setTimeout(() => {
        closeAskModal(modalId);
        // Reset form for next time
        setTimeout(() => {
          form.reset();
          form.querySelector('.ask-question__fields').style.display = '';
          submitBtn.style.display = '';
          if (statusEl) statusEl.setAttribute('hidden', '');
        }, 400);
      }, 4000);

    } else {
      throw new Error(`HTTP ${res.status}`);
    }
  } catch (err) {
    console.error('[AskAQuestion] Submit error:', err);
    showStatus(statusEl, 'error',
      'Something went wrong. Please try again or contact us directly.');

    // Reset button
    submitBtn.disabled = false;
    submitText.textContent = 'Send Question';
    spinner.setAttribute('hidden', '');
  }
}

function showStatus(el, type, message) {
  if (!el) return;
  el.textContent = message;
  el.className = `ask-question__status ask-question__status--${type}`;
  el.removeAttribute('hidden');
}

/* ── Event wiring ───────────────────────────────────────────── */

function init() {
  // Open triggers
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-modal-target]');
    if (trigger) {
      e.preventDefault();
      openAskModal(trigger.dataset.modalTarget);
    }
  });

  // Close button
  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-modal-close]');
    if (closeBtn) closeAskModal(closeBtn.dataset.modalClose);
  });

  // Click outside modal box
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('ask-question__overlay')) {
      const id = e.target.id;
      if (id) closeAskModal(id);
    }
  });

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.ask-question__overlay:not([hidden])').forEach(overlay => {
        closeAskModal(overlay.id);
      });
    }
  });

  // Form submit
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('.ask-question__form');
    if (!form) return;
    e.preventDefault();
    if (validateForm(form)) {
      // Get modal ID from the parent overlay (avoids hyphenated attr on {% form %} tag)
      const modalId = form.closest('.ask-question__overlay')?.id;
      submitForm(form, modalId);
    }
  });

  // Clear error on input
  document.addEventListener('input', (e) => {
    const input = e.target.closest('.ask-question__input');
    if (!input) return;
    input.classList.remove('is-invalid');
    const errorEl = input.closest('.ask-question__field')?.querySelector('.ask-question__error');
    if (errorEl) errorEl.textContent = '';
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

